import { useEffect } from "react";
import AnkenyHeroSection from "./components/HeroSection";
import AnkenyStickyNav from "./components/StickyNav";
import AnkenyProcessingTimeSection from "./components/ProcessingTimeSection";
import AnkenyServicesSection from "./components/ServicesSection";
import AnkenyLocalAreaSection from "./components/LocalAreaSection";
import AnkenyTestimonialsSection from "./components/TestimonialsSection";
import AnkenyAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import AnkenyComparisonSection from "./components/ComparisonSection";
import AnkenyMistakesSection from "./components/MistakesSection";
import AnkenyHowItWorksSection from "./components/HowItWorksSection";
import AnkenyFAQSection, { faqs } from "./components/FAQSection";
import AnkenyHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import AnkenyFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function AnkenyPage() {
  useEffect(() => {
    document.title = "Emergency Passport Ankeny IA — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/ankeny/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Ankeny, IA — as fast as 24 hours. A+ BBB rated, registered State Dept. courier serving manufacturing professionals, DMACC students, and growing Polk County families.");
    setMeta("geo.placename", "Ankeny, Iowa");
    setMeta("geo.region", "US-IA");
    setMeta("geo.position", "41.7318;-93.6044");
    setMeta("ICBM", "41.7318, -93.6044");

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Iowa", item: "https://fastpassportcenter.com/iowa/" },
          { "@type": "ListItem", position: 3, name: "Ankeny Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Emergency and expedited passport services for Ankeny, John Deere professionals, Casey's corporate, and the northern Des Moines metro. As fast as 24 hours.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: { "@type": "City", name: "Ankeny", containedInPlace: { "@type": "State", name: "Iowa" } },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" },
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: { "@type": "Answer", text: faq.a },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      document.getElementById("structured-data")?.remove();
      document.querySelector('link[rel="canonical"]')?.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <AnkenyHeroSection />
      <AnkenyStickyNav />
      <AnkenyProcessingTimeSection />
      <AnkenyServicesSection />
      <AnkenyLocalAreaSection />
      <AnkenyTestimonialsSection />
      <AnkenyAcceptanceFacilitiesSection />
      <AnkenyComparisonSection />
      <AnkenyMistakesSection />
      <AnkenyHowItWorksSection />
      <AnkenyFAQSection />
      <AnkenyHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/ankeny" state="IA" />
      <AnkenyFinalCTA />
    </div>
  );
}